import { BarChart3, Columns2, ImagePlus, QrCode, Tag } from 'lucide-react';

import { AdComponent, base, margin, shape, text, unit } from './shared';

/** Klocki wokół materiału: zdjęcia, kod QR, liczby i cennik. */
export const MEDIA_COMPONENTS: AdComponent[] = [

  {
    id: 'image-caption',
    label: 'Zdjęcie z podpisem',
    description: 'Ramka na zdjęcie i opis pod spodem',
    icon: ImagePlus,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const width = Math.round((scene.width - x * 2) * 0.55);
      const y = Math.round(scene.height * 0.34);
      const height = Math.round(width * 0.66);

      return [
        {
          ...base('Zdjęcie'),
          type: 'image',
          src: '',
          x,
          y,
          width,
          height,
          radius: Math.round(u * 0.025),
          fit: 'cover',
          circle: false,
        },
        text('Podpis zdjęcia', {
          text: 'Lobby po wdrożeniu Hotelspota',
          x,
          y: y + height + Math.round(u * 0.025),
          width,
          fontSize: Math.round(u * 0.024),
          fontFamily: fonts.body,
          fontWeight: 500,
          color: palette.accent,
        }),
      ];
    },
  },

  {
    id: 'qr',
    label: 'Miejsce na kod QR',
    description: 'Biały kwadrat z wezwaniem do skanowania',
    icon: QrCode,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const size = Math.round(u * 0.22);
      const x = Math.round(scene.width - margin(scene) - size);
      const y = Math.round(scene.height * 0.66);

      return [
        shape('Tło kodu', {
          x,
          y,
          width: size,
          height: size,
          fill: '#ffffff',
          radius: Math.round(size * 0.08),
        }),
        text('Podpowiedź kodu', {
          text: 'Wstaw tu kod QR',
          x,
          y: y + Math.round(size * 0.44),
          width: size,
          fontSize: Math.round(size * 0.1),
          fontFamily: fonts.body,
          fontWeight: 500,
          align: 'center',
          color: '#9a9a9a',
        }),
        text('Wezwanie', {
          text: 'Zeskanuj i zobacz demo',
          x: x - Math.round(size * 0.1),
          y: y + size + Math.round(u * 0.02),
          width: Math.round(size * 1.2),
          fontSize: Math.round(u * 0.022),
          fontFamily: fonts.body,
          fontWeight: 600,
          align: 'center',
          color: palette.light,
        }),
      ];
    },
  },

  {
    id: 'bars',
    label: 'Mini wykres',
    description: 'Trzy słupki rosnącego wyniku',
    icon: BarChart3,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const baseline = Math.round(scene.height * 0.72);
      const barWidth = Math.round(u * 0.07);
      const gap = Math.round(barWidth * 0.5);
      const heights = [0.4, 0.68, 1];
      const maxHeight = Math.round(u * 0.26);

      return [
        ...heights.map((ratio, index) => shape(`Słupek ${index + 1}`, {
          x: x + (barWidth + gap) * index,
          y: baseline - Math.round(maxHeight * ratio),
          width: barWidth,
          height: Math.round(maxHeight * ratio),
          fill: index === heights.length - 1 ? palette.accent : palette.mid,
          radius: Math.round(barWidth * 0.2),
        })),
        text('Podpis wykresu', {
          text: 'Zamówienia w kolejnych miesiącach',
          x,
          y: baseline + Math.round(u * 0.03),
          width: Math.round(scene.width * 0.6),
          fontSize: Math.round(u * 0.024),
          fontFamily: fonts.body,
          fontWeight: 500,
          color: palette.light,
        }),
      ];
    },
  },

  {
    id: 'two-columns',
    label: 'Dwie kolumny tekstu',
    description: 'Dwa bloki opisu obok siebie',
    icon: Columns2,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const total = scene.width - x * 2;
      const gap = Math.round(u * 0.05);
      const columnWidth = Math.round((total - gap) / 2);
      const y = Math.round(scene.height * 0.55);
      const fontSize = Math.round(u * 0.026);

      const column = (index: number, title: string, body: string) => [
        text(`Tytuł kolumny ${index + 1}`, {
          text: title,
          x: x + (columnWidth + gap) * index,
          y,
          width: columnWidth,
          fontSize: Math.round(u * 0.032),
          fontFamily: fonts.subtitle,
          fontWeight: 800,
          color: palette.accent,
        }),
        text(`Treść kolumny ${index + 1}`, {
          text: body,
          x: x + (columnWidth + gap) * index,
          y: y + Math.round(u * 0.06),
          width: columnWidth,
          fontSize,
          fontFamily: fonts.body,
          fontWeight: 400,
          lineHeight: 1.45,
          color: palette.light,
        }),
      ];

      return [
        ...column(0, 'Dla gościa', 'Zamawia z telefonu, bez kolejki i bez telefonu do recepcji.'),
        ...column(1, 'Dla obiektu', 'Zgłoszenia w jednym miejscu, rozliczenie bez papierów.'),
      ];
    },
  },

  {
    id: 'price',
    label: 'Cena',
    description: 'Kwota z dopiskiem i notką',
    icon: Tag,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const y = Math.round(scene.height * 0.6);

      return [
        text('Dopisek ceny', {
          text: 'już od',
          x,
          y,
          width: Math.round(scene.width * 0.4),
          fontSize: Math.round(u * 0.026),
          fontFamily: fonts.body,
          fontWeight: 500,
          uppercase: true,
          letterSpacing: Math.round(u * 0.004),
          color: palette.accent,
        }),
        text('Kwota', {
          text: '299 zł / mies.',
          x,
          y: y + Math.round(u * 0.04),
          width: Math.round(scene.width * 0.7),
          fontSize: Math.round(u * 0.075),
          fontFamily: fonts.headline,
          fontWeight: 900,
          lineHeight: 1.05,
          color: palette.light,
        }),
        text('Notka', {
          text: 'Bez opłaty wdrożeniowej, umowa na miesiąc.',
          x,
          y: y + Math.round(u * 0.14),
          width: Math.round(scene.width * 0.7),
          fontSize: Math.round(u * 0.024),
          fontFamily: fonts.body,
          fontWeight: 400,
          color: '#dbeae6',
        }),
      ];
    },
  },
];
